import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '@/features/bundleDiscount/bundleDiscount.constants'
import { isExistingMember, shouldShowAccidentDisclaimer } from '@/features/bundleDiscount/bundleDiscount.utils'
import { BundleDiscountOverview } from '@/features/bundleDiscount/components/BundleDiscountOverview/BundleDiscountOverview'
import { BundleDiscountProgress } from '@/features/bundleDiscount/components/BundleDiscountProgress/BundleDiscountProgress'
import { DiscountInfoCard } from '@/features/CrossSell/components/DiscountInfoCard'
import { useDiscountNotification } from '@/features/CrossSell/hooks/useDiscountNotification'
import { type ProductOfferFragment, RedeemedCampaignTypeV2 } from '@/services/graphql/generated'
import { type PriceIntent } from '@/services/priceIntent/priceIntent.types'
import { useShopSessionValueOrThrow } from '@/services/shopSession/ShopSessionContext'
import { getSummarisedPrice } from '@/utils/getOfferPrice'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { Badge } from 'ui/src/components/Badge/Badge'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { useOfferToEdit } from '../ProductPage/useOfferToEdit'
import { TextWithLink } from '../TextWithLink'
import { TotalPrice } from '../TotalPrice/TotalPrice'
import { dialogContent, divider } from './DiscountBreakdownDialog.css'

type Props = {
  offer: ProductOfferFragment
  priceIntent: PriceIntent
}

export function DiscountBreakdownDialog({ offer, priceIntent }: Props) {
  const { t } = useTranslation(['common', 'purchase-form'])
  const shopSession = useShopSessionValueOrThrow()
  const locale = useRoutingLocale()

  const price = getSummarisedPrice(offer.cost)
  const selectedAddons = offer.addons.filter((addon) => addon.isSelected)
  const isHomeStudent = offer.product.name === 'SE_APARTMENT_STUDENT'
  const badgeText = isHomeStudent ? offer.variant.displayNameSubtype : offer.variant.displayNameTier
  const redeemedCampaignV2 = shopSession.cart.redeemedCampaignV2
  const discountNotification = useDiscountNotification(offer, priceIntent)
  const offerToEdit = useOfferToEdit()

  const isCurrentOfferEligible = offer.bundleDiscount.isEligible
  const isEditingCartItem = offerToEdit?.isInCart
  const eligibleProductCount = shopSession.cart.bundleDiscount.eligibleProductCount
  const shouldAnimateBundleProgress = isCurrentOfferEligible
  // We need to add the currently viewed offer in the bundle discount progress count unless it's being edited
  const additionalCount = isCurrentOfferEligible && !isEditingCartItem ? 1 : 0
  const totalProductCount = (shopSession.cart.bundleDiscount.eligibleProductCount ?? 0) + additionalCount

  // Only case where we don't want to show the bundle discount section is
  // for accident insurance and there are no eligible products in cart
  const showBundleDiscountSection = !(isCurrentOfferEligible === false && eligibleProductCount === 0)

  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild>
        <button className={sprinkles({ cursor: 'pointer' })}>
          {t('purchase-form:DISCOUNT_BREAKDOWN_DIALOG_BUTTON_LABEL')}
        </button>
      </Dialog.Trigger>
      <LargeDialog.Content className={dialogContent}>
        <LargeDialog.CloseIconButton aria-label={t('DIALOG_CLOSE')} />
        <div className={yStack({ gap: 'md' })}>
          <div>
            <Heading variant="standard.18" as="h2">
              {t('purchase-form:DISCOUNT_BREAKDOWN_DIALOG_TITLE')}
            </Heading>
          </div>

          <div>
            <DetailsList.Root size="xs" gap="xs">
              <DetailsList.Item>
                <DetailsList.Label asChild>
                  <div className={xStack({ alignItems: 'center', gap: 'xxs' })}>
                    <span className={sprinkles({ flex: { _: 1, xs: 'auto' } })}>{offer.product.displayNameFull}</span>

                    {badgeText && (
                      <div className={sprinkles({ flex: 1 })}>
                        <Badge size="small" variant="grey2">
                          {badgeText}
                        </Badge>
                      </div>
                    )}
                  </div>
                </DetailsList.Label>
                <DetailsList.Value>
                  <AccessiblePrice
                    price={{
                      currencyCode: offer.cost.baseInsuranceGross.currencyCode,
                      amount: offer.cost.baseInsuranceGross.amount,
                    }}
                  />
                </DetailsList.Value>
              </DetailsList.Item>

              {selectedAddons.length > 0 &&
                selectedAddons.map((addon) => (
                  <DetailsList.Item key={addon.id}>
                    <DetailsList.Label className={xStack({ alignItems: 'center', gap: 'xxs' })}>
                      {addon.variant.displayName}
                    </DetailsList.Label>
                    <DetailsList.Value>
                      <AccessiblePrice
                        price={{ currencyCode: addon.grossCost.currencyCode, amount: addon.grossCost.amount }}
                      />
                    </DetailsList.Value>
                  </DetailsList.Item>
                ))}

              {offer.cost.discountsV2.map(({ itemDiscount: discount }) => (
                <DetailsList.Item key={discount.campaignCode}>
                  <DetailsList.Label>{discount.displayName}</DetailsList.Label>
                  <DetailsList.Value className={xStack({ gap: 'xxs' })}>
                    <Text as="span" size="xs">
                      {discount.displayValue}
                    </Text>
                    {discount.tooltip && (
                      <ToggleTip
                        arrow={true}
                        sideOffset={2}
                        collisionPadding={8}
                        Trigger={
                          <IconButton
                            data-no-hover
                            Icon={<InfoIcon size="1rem" color={tokens.colors.fills.opaque.secondary} />}
                          />
                        }
                      >
                        <Text size="xs" color="textNegative" align="center">
                          {discount.tooltip}
                        </Text>
                      </ToggleTip>
                    )}
                  </DetailsList.Value>
                </DetailsList.Item>
              ))}

              {redeemedCampaignV2?.type === RedeemedCampaignTypeV2.Referral && (
                <DetailsList.Item>
                  <DetailsList.Label>
                    {t('purchase-form:DISCOUNT_BREAKDOWN_DIALOG_REFERRAL_DISPLAY_NAME')}
                  </DetailsList.Label>
                  <div className={xStack({ alignItems: 'center', gap: 'xxs' })}>
                    <DetailsList.Value>{redeemedCampaignV2.displayValue}</DetailsList.Value>
                    <ToggleTip
                      arrow={true}
                      sideOffset={4}
                      collisionPadding={8}
                      Trigger={
                        <IconButton
                          data-no-hover
                          Icon={<InfoIcon size="1rem" color={tokens.colors.fills.opaque.secondary} />}
                        />
                      }
                    >
                      <Text size="xs" color="textNegative" align="center">
                        {t('purchase-form:DISCOUNT_BREAKDOWN_DIALOG_REFERRAL_TOOLTIP')}
                      </Text>
                    </ToggleTip>
                  </div>
                </DetailsList.Item>
              )}
            </DetailsList.Root>
          </div>

          {discountNotification ? <DiscountInfoCard {...discountNotification} /> : <hr className={divider} />}

          <TotalPrice {...price} label={t('common:YOUR_PRICE')} />

          {showBundleDiscountSection && (
            <>
              <hr className={divider} />

              <div className={yStack({ gap: 'md' })}>
                <div className={xStack({ alignItems: 'center', gap: 'xs', justifyContent: 'space-between' })}>
                  <Heading as="h3" variant="standard.18">
                    {t('purchase-form:DISCOUNT_BREAKDOWN_DIALOG_BUNDLE_DISCOUNT_TITLE')}
                  </Heading>
                  <ToggleTip
                    Trigger={
                      <IconButton
                        data-no-hover
                        Icon={<InfoIcon size="1.125rem" color={tokens.colors.fills.opaque.secondary} />}
                      />
                    }
                  >
                    <TextWithLink
                      size="xs"
                      color="textNegative"
                      align="center"
                      href={PageLink.bundleDiscount({ locale })}
                    >
                      {t('purchase-form:BUNDLE_DISCOUNT_SECTION_TOOLTIP', { percentage: BUNDLE_DISCOUNT_PERCENTAGE })}
                    </TextWithLink>
                  </ToggleTip>
                </div>

                <div className={yStack({ gap: 'lg' })}>
                  {isExistingMember(shopSession) ? (
                    <BundleDiscountOverview />
                  ) : (
                    <BundleDiscountProgress
                      eligibleInsuranceCount={totalProductCount}
                      showStatus={true}
                      shouldAnimate={shouldAnimateBundleProgress}
                    />
                  )}

                  {shouldShowAccidentDisclaimer(shopSession, priceIntent) && (
                    <Text as="p" size="xs" color="textSecondary" align="center">
                      {t('BUNDLE_ACCIDENT_DISCLAIMER')}
                    </Text>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
